import { useMemo, useState } from 'react';
import { formatDate, formatDateTime } from '../../lib/api';
import type { AnchorsResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner } from '../../ui/components';

export function Integrity() {
  const q = useApiQuery<AnchorsResponse>('/api/v1/opengov/anchors/latest');
  const [moduleFilter, setModuleFilter] = useState('');

  const modules = useMemo(() => {
    const set = new Set((q.data?.items ?? []).map((a) => a.moduleCode));
    return [...set].sort();
  }, [q.data]);

  const rows = (q.data?.items ?? []).filter((a) => !moduleFilter || a.moduleCode === moduleFilter);

  return (
    <div>
      <PageHeader
        title="Platform Integrity"
        subtitle="Daily cryptographic anchors of every module's audit trail — proof that historical records have not been silently altered."
      />

      <div className="card mb-6 p-5">
        <p className="text-sm text-slate-600">
          Every action taken across One Gov Seychelles — a payment released, a permit issued, a record changed — is
          written to that module's append-only audit log. Once a day, each module's log for that day is hashed into
          a single <span className="font-mono text-xs">dayRootHash</span> and anchored here. Anyone can use these
          anchors, together with a module's own verify endpoint, to confirm that no entry in a given day's log has
          been altered after the fact.
        </p>
      </div>

      {modules.length > 0 ? (
        <div className="mb-4">
          <label className="block max-w-xs">
            <span className="field-label">Module</span>
            <select className="field-input" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
              <option value="">All modules</option>
              {modules.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {q.error ? <ErrorNote message={q.error} /> : null}
      <div className="card overflow-x-auto">
        {q.loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="No anchors published yet" hint="Anchors are produced once a day per module." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Module</th>
                <th className="table-head">Date</th>
                <th className="table-head text-right">Sequence range</th>
                <th className="table-head">Day root hash</th>
                <th className="table-head">Published to</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs font-semibold">{a.moduleCode}</td>
                  <td className="table-cell text-slate-500">{formatDate(a.date)}</td>
                  <td className="table-cell text-right tabular-nums text-slate-500">
                    {a.fromSeq} – {a.toSeq}
                  </td>
                  <td className="table-cell">
                    <span className="font-mono text-xs text-slate-600" title={a.dayRootHash}>
                      {a.dayRootHash.slice(0, 16)}…
                    </span>
                  </td>
                  <td className="table-cell text-xs text-slate-500">{a.publishedTo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {q.data && q.data.items.length > 0 ? (
        <p className="mt-2 text-xs text-slate-400">
          Most recent anchor generated {formatDateTime(q.data.items[0]!.createdAt)}.
        </p>
      ) : null}
    </div>
  );
}
